import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import PartnerLayout from "./PartnerLayout";
import {
    Coin,
    Clock,
    PaperPlaneTilt,
    Trophy,
    ArrowRight,
    Sparkle,
} from "@phosphor-icons/react";

const fmtINR = (n) => {
    if (!n && n !== 0) return "—";
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    return `₹${n.toLocaleString("en-IN")}`;
};

const STATUS_TONE = {
    submitted: "bg-blue-50 text-blue-700 border-blue-200",
    viewed: "bg-purple-50 text-purple-700 border-purple-200",
    shortlisted: "bg-amber-50 text-amber-700 border-amber-200",
    interviewing: "bg-cyan-50 text-cyan-700 border-cyan-200",
    offered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    hired: "bg-emerald-100 text-emerald-800 border-emerald-300",
    rejected: "bg-red-50 text-red-700 border-red-200",
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "short",
        });
    } catch { return iso; }
};

function KpiCard({ label, value, sub, tone, testId }) {
    return (
        <div data-testid={testId} className="bg-white border border-[#E5E5E5] p-4 sm:p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">{label}</p>
            <p className={`font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2 ${tone || "text-[#0A0A0A]"}`}>{value}</p>
            {sub && <p className="text-xs text-[#A3A3A3] mt-1">{sub}</p>}
        </div>
    );
}

export default function PartnerDashboard() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/partner/me/dashboard");
            setData(r.data);
        } catch (e) {
            setErr(describeAxiosError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <PartnerLayout>
            <div className="space-y-8" data-testid="partner-dashboard-page">
                <header>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0891B2]">Partner portal</p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A] mt-1">
                        {data?.activation?.business_name
                            ? `${data.activation.business_name}, welcome back.`
                            : "Welcome back."}
                    </h1>
                    <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                        Submit candidates to open roles across every agency on NaukriDaddy. Get paid when they get placed.
                    </p>
                </header>

                {err && (
                    <div data-testid="partner-dashboard-error" className="border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
                        {err}
                    </div>
                )}

                {loading && !data && (
                    <div className="text-sm text-[#A3A3A3]">Loading…</div>
                )}

                {data && (
                    <>
                        {/* KPI grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                            <KpiCard
                                testId="partner-kpi-earned-paid"
                                label="Earned · paid"
                                value={fmtINR(data.kpis.earned_paid_inr)}
                                sub="Cleared to your bank"
                                tone="text-emerald-600"
                            />
                            <KpiCard
                                testId="partner-kpi-earned-pending"
                                label="Pending payout"
                                value={fmtINR(data.kpis.earned_pending_inr)}
                                sub="Placement confirmed · payment queued"
                                tone="text-amber-600"
                            />
                            <KpiCard
                                testId="partner-kpi-submissions"
                                label="Total submissions"
                                value={data.kpis.total_submissions}
                                sub={
                                    data.kpis.total_submissions === 0
                                        ? "Submit your first candidate below"
                                        : "Across all open roles"
                                }
                            />
                            <KpiCard
                                testId="partner-kpi-placements"
                                label="Placements"
                                value={data.kpis.placements}
                                sub="Candidates hired via you"
                                tone="text-[#0891B2]"
                            />
                        </div>

                        {/* Big CTA — go to marketplace */}
                        {data.kpis.total_submissions === 0 && (
                            <div data-testid="partner-empty-cta"
                                 className="bg-[#0A0A0A] text-white p-6 sm:p-8 relative overflow-hidden">
                                <div className="relative z-10 max-w-2xl">
                                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#0891B2] mb-2 inline-flex items-center gap-1">
                                        <Sparkle size={11} weight="fill" /> Start earning
                                    </p>
                                    <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter">
                                        Browse open roles and submit your first candidate.
                                    </h2>
                                    <p className="text-white/70 text-sm mt-2">
                                        First-submitter-wins ownership. If your candidate gets placed, the payout is yours — no cold pitches, no client chasing.
                                    </p>
                                    <Link
                                        to="/partner/marketplace"
                                        data-testid="partner-open-marketplace-cta"
                                        className="mt-5 inline-flex items-center gap-2 bg-[#0891B2] hover:bg-[#0e7490] text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors"
                                    >
                                        See open roles <ArrowRight size={14} weight="bold" />
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Recent submissions */}
                        {data.recent_submissions.length > 0 && (
                            <section>
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="font-heading font-black text-lg sm:text-xl tracking-tight">Recent submissions</h2>
                                    <Link to="/partner/submissions"
                                          data-testid="partner-view-all-submissions"
                                          className="text-xs font-bold uppercase tracking-wider text-[#0891B2] hover:text-[#0A0A0A]">
                                        View all →
                                    </Link>
                                </div>
                                <div className="space-y-2">
                                    {data.recent_submissions.map((s) => (
                                        <div key={s.id}
                                             data-testid={`partner-recent-sub-${s.id}`}
                                             className="bg-white border border-[#E5E5E5] p-4 flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="font-bold truncate">{s.candidate_name}</p>
                                                <p className="text-[11px] text-[#A3A3A3] truncate">for {s.job_title}</p>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 border ${STATUS_TONE[s.status] || STATUS_TONE.submitted}`}>
                                                    {s.status}
                                                </span>
                                                <span className="hidden sm:inline text-[10px] text-[#A3A3A3]">{fmtDate(s.submitted_at)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* How it works */}
                        <section className="bg-white border border-[#E5E5E5] p-5 sm:p-6">
                            <h2 className="font-heading font-black text-lg sm:text-xl tracking-tight mb-3">
                                How you get paid
                            </h2>
                            <ol className="space-y-3 text-sm text-[#525252]">
                                <li className="flex gap-3">
                                    <span className="font-heading font-black text-[#0891B2] shrink-0">1.</span>
                                    <span>Browse the marketplace and pick roles that match your talent pool.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-heading font-black text-[#0891B2] shrink-0">2.</span>
                                    <span>Submit the candidate with contact info and a short note — first submitter wins ownership.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-heading font-black text-[#0891B2] shrink-0">3.</span>
                                    <span>Track status in "My submissions" as the recruiter moves your candidate through the pipeline.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-heading font-black text-[#0891B2] shrink-0">4.</span>
                                    <span>On placement, we generate the GST invoice automatically and the payout appears in your dashboard.</span>
                                </li>
                            </ol>
                        </section>
                    </>
                )}
            </div>
        </PartnerLayout>
    );
}
